import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import {
  HomeIcon,
  UserGroupIcon,
  UsersIcon,
  ShieldExclamationIcon,
  Cog6ToothIcon,
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  XMarkIcon,
  ChartBarIcon,
  QuestionMarkCircleIcon,
  CogIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  UserCircleIcon,
  UserPlusIcon
} from '@heroicons/react/24/outline';
import TodoPanel from '../TodoPanel';

const MainLayout = ({ children }) => {
  const { logout, user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  // Load todo panel state from localStorage
  const [todoPanelOpen, setTodoPanelOpen] = useState(() => {
    const saved = localStorage.getItem('todoPanelOpen');
    return saved !== null ? JSON.parse(saved) : true; // Default to open
  });

  // Save todo panel state to localStorage
  useEffect(() => {
    localStorage.setItem('todoPanelOpen', JSON.stringify(todoPanelOpen));
  }, [todoPanelOpen]);

  const toggleTodoPanel = () => {
    setTodoPanelOpen(!todoPanelOpen);
  };

  const navigation = [
    { name: 'Dashboard', href: '/', icon: HomeIcon },
    { name: 'Accounts', href: '/accounts', icon: UsersIcon },
    ...(['admin', 'manager'].includes(user?.role) ? [{ name: 'Мои каналы', href: '/my-channels', icon: UserGroupIcon }] : []),
    { name: 'Groups', href: '/groups', icon: UserGroupIcon },
    { name: 'Projects', href: '/projects', icon: ShieldExclamationIcon },
    { name: 'Agents', href: '/agents', icon: UserCircleIcon },
    { name: 'Blacklist', href: '/blacklist', icon: ShieldExclamationIcon },
    { name: 'Metrics', href: '/metrics', icon: ChartBarIcon },
    { name: 'Processes', href: '/processes', icon: CogIcon },
    { name: 'Инструкции', href: '/instructions', icon: QuestionMarkCircleIcon },
    { name: 'О продукте', href: '/about-product', icon: InformationCircleIcon },
    { name: 'Admin Users', href: '/admin-users', icon: UserPlusIcon },
    { name: 'Settings', href: '/settings', icon: Cog6ToothIcon },
  ];

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const openHistorySearch = () => {
    window.dispatchEvent(new CustomEvent('open-global-search', {
      detail: { mode: 'telegram-history' },
    }));
  };

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 lg:hidden ${sidebarOpen ? 'block' : 'hidden'}`} role="dialog" aria-modal="true">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" aria-hidden="true" onClick={() => setSidebarOpen(false)}></div>
        
        <div className="relative flex flex-col flex-1 w-full max-w-xs bg-white">
          <div className="absolute top-0 right-0 pt-2 -mr-12">
            <button
              type="button"
              className="flex items-center justify-center w-10 h-10 ml-1 rounded-full focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white"
              onClick={() => setSidebarOpen(false)}
            >
              <span className="sr-only">Close sidebar</span>
              <XMarkIcon className="w-6 h-6 text-white" aria-hidden="true" />
            </button>
          </div>
          
          <div className="flex-1 h-0 pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1 className="text-xl font-bold text-primary-600">Telegram Bot Farm</h1>
            </div>
            <div className="px-2 mt-4">
              <button
                type="button"
                onClick={openHistorySearch}
                className="group flex w-full items-center rounded-md bg-gray-900 px-2 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                <MagnifyingGlassIcon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-200" aria-hidden="true" />
                History Search
              </button>
            </div>
            <nav className="px-2 mt-5 space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    location.pathname === item.href
                      ? 'bg-gray-100 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 text-base font-medium rounded-md`}
                >
                  <item.icon
                    className={`${
                      location.pathname === item.href ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-4 flex-shrink-0 h-6 w-6`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex flex-shrink-0 p-4 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className="flex items-center w-full px-2 py-2 text-base font-medium text-gray-600 rounded-md hover:bg-gray-50 hover:text-gray-900 group"
            >
              <ArrowRightOnRectangleIcon className="w-6 h-6 mr-4 text-gray-400 group-hover:text-gray-500" />
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Static sidebar for desktop */}
      <div className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0">
        <div className="flex flex-col flex-1 min-h-0 bg-white border-r border-gray-200">
          <div className="flex flex-col flex-1 pt-5 pb-4 overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-4">
              <h1 className="text-xl font-bold text-primary-600">Telegram Bot Farm</h1>
            </div>
            <div className="px-2 mt-4">
              <button
                type="button"
                onClick={openHistorySearch}
                className="group flex w-full items-center rounded-md bg-gray-900 px-2 py-2 text-sm font-medium text-white hover:bg-gray-800"
              >
                <MagnifyingGlassIcon className="mr-3 h-5 w-5 flex-shrink-0 text-gray-200" aria-hidden="true" />
                History Search
              </button>
            </div>
            <nav className="flex-1 px-2 mt-5 space-y-1 bg-white">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.href}
                  className={`${
                    location.pathname === item.href
                      ? 'bg-gray-100 text-primary-600'
                      : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                  } group flex items-center px-2 py-2 text-sm font-medium rounded-md`}
                >
                  <item.icon
                    className={`${
                      location.pathname === item.href ? 'text-primary-500' : 'text-gray-400 group-hover:text-gray-500'
                    } mr-3 flex-shrink-0 h-6 w-6`}
                    aria-hidden="true"
                  />
                  {item.name}
                </Link>
              ))}
            </nav>
          </div>
          <div className="flex flex-shrink-0 p-4 border-t border-gray-200">
            <div className="flex-shrink-0 block w-full group">
              <div className="flex items-center">
                <div>
                  <div className="flex items-center justify-center w-10 h-10 text-white bg-primary-600 rounded-full">
                    {user?.username?.charAt(0).toUpperCase() || 'A'}
                  </div>
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-700 group-hover:text-gray-900">
                    {user?.username || 'Admin'}
                  </p>
                  <button
                    onClick={handleLogout}
                    className="text-xs font-medium text-gray-500 group-hover:text-gray-700"
                  >
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className={`lg:pl-64 flex flex-col flex-1 transition-all duration-300 ${todoPanelOpen ? 'xl:pr-80' : ''}`}>
        <div className="sticky top-0 z-10 flex flex-shrink-0 h-16 bg-white border-b border-gray-200 lg:hidden">
          <button
            type="button"
            className="px-4 text-gray-500 border-r border-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500 lg:hidden"
            onClick={() => setSidebarOpen(true)}
          >
            <span className="sr-only">Open sidebar</span>
            <Bars3Icon className="w-6 h-6" aria-hidden="true" />
          </button>
          <div className="flex items-center justify-center flex-1 px-4">
            <h1 className="text-lg font-semibold text-primary-600">Telegram Bot Farm</h1>
          </div>
          <button
            type="button"
            className="px-4 text-gray-500 border-l border-gray-200 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary-500"
            onClick={openHistorySearch}
          >
            <span className="sr-only">Open history search</span>
            <MagnifyingGlassIcon className="w-6 h-6" aria-hidden="true" />
          </button>
        </div>

        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          {children}
        </main>
      </div>

      {/* Todo Panel - only visible on xl screens and above */}
      <div className="hidden xl:block">
        <TodoPanel isOpen={todoPanelOpen} onToggle={toggleTodoPanel} />
      </div>
    </div>
  );
};

export default MainLayout;
